import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { useDispatch } from "react-redux";
import CommonIcons from "@base/assets/icons";
import CommonStyles from "@components/CommonStyles";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";

export const MAX_ATTACHMENTS = 5;

const isImageFile = (file: File) => file.type.startsWith("image/");

/** A small preview for a picked image; a PDF gets the document icon. */
function AttachmentThumbnail({ file }: { file: File }) {
  const [url, setUrl] = useState<string>();

  useEffect(() => {
    if (!isImageFile(file)) {
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return url ? <img className="w-10 h-10 rounded-lg object-cover shrink-0" src={url} alt="" /> : <CommonIcons.IconTextSnippet />;
}

const ACCEPTED = { "image/*": [".png", ".jpg", ".jpeg"], "application/pdf": [".pdf"] };

interface IProps {
  files: File[];
  onChange: (files: File[]) => void;
}

/** Previous records the doctor can read before the visit - optional, images or PDFs, at most five. */
export default function AttachmentPicker({ files, onChange }: IProps) {
  //! state
  const { t } = useTranslation();
  const dispatch = useDispatch();

  //! function
  const onDrop = useCallback(
    (accepted: File[], rejected: unknown[]) => {
      // Same name and size twice over is the same document picked twice.
      const isDuplicate = (file: File) => files.some((existing) => existing.name === file.name && existing.size === file.size);
      const fresh = accepted.filter((file) => !isDuplicate(file));
      const room = MAX_ATTACHMENTS - files.length;

      // One message at a time, the most pressing first: over the limit, then a wrong type, then a repeat.
      const problem =
        fresh.length > room
          ? "packageBooking.tooManyAttachments"
          : rejected.length
            ? "packageBooking.unsupportedAttachment"
            : accepted.length > fresh.length
              ? "packageBooking.duplicateAttachment"
              : undefined;

      if (problem) {
        dispatch(setAlertMessage({ type: "error", message: t("Notification"), description: t(problem) }));
      }

      onChange([...files, ...fresh.slice(0, Math.max(room, 0))]);
    },
    [files, onChange, dispatch, t],
  );

  const { getRootProps, getInputProps } = useDropzone({ accept: ACCEPTED, onDrop, disabled: files.length >= MAX_ATTACHMENTS });

  //! render
  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between items-center gap-2">
        <span className="text-common-maintext text-sm font-medium font-['Inter']">{t("packageBooking.attachments")}</span>
        <span className="text-common-subtext text-xs font-normal font-['Inter']">{`${files.length}/${MAX_ATTACHMENTS}`}</span>
      </div>

      <CommonStyles.TypographyUI use="paragraph" className="text-common-subtext text-xs font-normal font-['Inter'] !mb-0">
        {t("packageBooking.attachmentsHint")}
      </CommonStyles.TypographyUI>

      <div
        {...getRootProps()}
        className={`w-full border border-dashed border-common-divider rounded-lg p-4 text-center text-sm font-normal font-['Inter'] ${
          files.length >= MAX_ATTACHMENTS ? "text-common-subtext cursor-not-allowed opacity-50" : "text-common-blue700 cursor-pointer"
        }`}
      >
        <input {...getInputProps()} />
        {t("packageBooking.addAttachment")}
      </div>

      {files.map((file, index) => (
        <div key={`${file.name}-${index}`} className="flex items-center gap-3 bg-common-background rounded-lg p-2">
          <AttachmentThumbnail file={file} />
          <div className="grow flex flex-col min-w-0">
            <span className="text-common-maintext text-sm font-normal font-['Inter'] line-clamp-1">
              {file.name || t("packageBooking.attachmentFallbackName")}
            </span>
            <span className="text-common-subtext text-xs font-normal font-['Inter']">
              {isImageFile(file) ? t("packageBooking.attachmentImage") : t("packageBooking.attachmentPdf")}
            </span>
          </div>
          <CommonStyles.ButtonUI size="small" type="link" danger onClick={() => onChange(files.filter((_, idx) => idx !== index))}>
            {t("Delete")}
          </CommonStyles.ButtonUI>
        </div>
      ))}
    </div>
  );
}
